import React, { useEffect, useRef, useState } from 'react';
import { ZakekeViewer, useZakeke } from 'zakeke-configurator-react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  PlatformsType,
  ViewerOptionInterface,
  ViewerItemInterface,
  currentLanguage,
  ViewerCustomizationInterface,
  instanceOfViewerGroupAttribute,
  instanceOfViewerGroupStep,
  CustomizationsState,
  instanceOfViewerGroupCustomizations,
  ViewerGroupCustomizationInterface,
  SPECIAL_OPTION,
  ViewerAttributeInputInterface,
} from '../../core';
import * as customizations from '../../core/services/customizations';
import { useAr, useViewerOptions, useShare, useCart, useLanguage, useExploded, useFullscreenStatus } from '../../hooks';

import Cameras from '../../components/Cameras';
import Preview, { previewAttributes } from '../../components/Preview';
import Zoom from '../../components/Zoom';
import BubbleButton from '../../components/BubbleButton';
import ARChoiceModal, { openArChoiceModal } from '../../components/ARChoiceModal';
import QrCodeModal, { openQrCodeModal } from '../../components/QrCodeModal';
import ImageModal, { openImageModal } from '../../components/ImageModal';
import ShareModal, { openShareModal } from '../../components/ShareModal';
import Menu from '../../components/Menu';

import ViewerIcon from '../../components/ViewerIcon';
import { Ar as ArIcon, FullScreen as FullScreenIcon, IconOpen } from '../../icons';

import styles from './Viewer.module.scss';
import { additionalPropsContext, useCustomizationParams } from '../../context/ViewerContext';
import Loader from '../../components/Loader';
import { customStylesTheme } from '../../core/utils/functions';
import { useAdditionalProps } from '../../hooks/use-additional-props';
import AddTextDialog from '../../components/MenuChoiceCustomizations/AddTextDialog';
import ErrorDialog from '../../components/MenuChoiceCustomizations/ErrorDialog';
import { EditTextItem } from '../../components/MenuChoiceCustomizations/ItemText';
import { BULK_PRICES } from '../../core/constants/constants';
import useBulkLimit from '../../hooks/use-bulk-limit';
import ImagesGalleryDialog from '../../components/MenuChoiceCustomizations/ImagesGalleryDialog';
import { usePdf } from '../../hooks/use-pdf';
import PdfModal, { openPdfModal } from '../../components/PdfModal';

const Viewer: React.FC = () => {
  const [t] = useTranslation();
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const fullScreenRef = useRef<HTMLDivElement | null>(null);
  const { zoomIn, zoomOut, productCode, updateView, culture, currency, loadComposition, getPDF } = useZakeke();
  const { language } = useLanguage();
  const { isMobile: isArMobile, isEnable: isArEnable, isArLoading, showArMobile, getPlatformArUrl } = useAr();
  const { isExploded, hasExploded, toggleExploded } = useExploded();
  const [totalPrice, setTotalPrice] = useState<string>();
  const {
    isSceneReady,
    isLoading,
    viewerOptions,
    viewerState,
    bulkData,
    resetBulkDataValues,
    setBulkDataOptionValue,
    setBulkDataAttributePrice,
    onSelectedOption,
    onSaveText,
    onSaveFont,
    onSetItemImage,
    onAddItemText,
    onAddItemImage,
    onRemoveItem,
    setCustomizationAssets,
  } = useViewerOptions();
  const { getShareUrl, share, isLoading: isShareLoading } = useShare();
  const { getPdfUrl, isLoading: isPdfLoading } = usePdf();
  const { isLoading: isCartLoading, price, add: addToCart, rawPrice, setPrice } = useCart();
  const [isFullscreen, setIsFullscreen] = useFullscreenStatus(fullScreenRef);
  const label = isExploded ? t('EXPOSED_CLOSE') : t('EXPOSED_OPEN');
  const { addAttributesToAdditionalProps } = useAdditionalProps({ groups: viewerOptions.groups });

  const { customizationParams, setCustomizationParams } = useCustomizationParams();
  const { additionalProps } = additionalPropsContext();
  const [previewImage, setPreviewImage] = useState<previewAttributes>();
  const [customPrices, setCustomPrices] = useState<any>(null);

  const [customizationsState, setCustomizationsState] = useState<CustomizationsState>({
    selectedCarouselSlide: 0,
    moveElements: false,
    slidesToShow: window.innerWidth <= 1600 ? 3 : 4,
    forceUpdate: false,
    showAddTextDialog: false,
    showErrorDialog: false,
    imageGalery: {
      showAddImageDialog: false,
      currentAction: 'ADD',
      selectedItem: null,
    },
    isLoading: false,
    actualAreaId: 0,
    isUploadDisabled: false,
    totalItems: 0,
  });

  const { disableCart } = useBulkLimit(bulkData);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data) {
        const eventData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (eventData.type === 'custom_price') {
          setCustomPrices(eventData.data.data);
        }
      }
    };

    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, []);

  // add title to the viewer. this title come from WordPress query string
  interface UrlParamsInterface {
    title: string | null;
    descriptionFieldValue: string | null;
    productDescription: string | null;
  }
  const [urlParams, setUrlParams] = useState<UrlParamsInterface>();
  useEffect(() => {
    if (!urlParams) {
      const urlParamsGet = new URLSearchParams(window.location.search);
      const customizationTltle = urlParamsGet.get('title');
      const descriptionFieldValue = urlParamsGet.get('descriptionField');
      const productDescription = urlParamsGet.get('product_description');

      setUrlParams({
        title: customizationTltle,
        descriptionFieldValue: descriptionFieldValue,
        productDescription: productDescription,
      });
    }
    // eslint-disable-next-line
  }, [urlParams]);

  useEffect(() => {
    const customizationsGroup = viewerOptions.groups.find((group) => instanceOfViewerGroupCustomizations(group)) as ViewerGroupCustomizationInterface;
    if (!customizationsGroup) return;

    // count all the areas inside customizationsGroup.areas and then count all the items inside customizationsGroup.areas
    const count = customizationsGroup.items.length;

    if (count !== undefined && count !== customizationsState.totalItems) {
      setCustomizationsState({
        ...customizationsState,
        totalItems: count,
      });
    }
  }, [viewerOptions.groups]);

  useEffect(() => {
    addAttributesToAdditionalProps();
  }, [viewerOptions.groups]);

  useEffect(() => {
    if (!!customizationParams) {
      customStylesTheme(customizationParams?.custom_styles);
    }
  }, [customizationParams]);

  const onPlatformSelected = async (platform: PlatformsType) => {
    try {
      openQrCodeModal({
        url: await getPlatformArUrl(platform),
      });
    } catch (ex) {
      console.error(ex);
      alert();
    }
  };

  useEffect(() => {
    if (customizationParams?.maxCustomizationsAreas !== undefined && customizationParams?.maxCustomizationsAreas > 0) {
      if (customizationsState.totalItems && customizationsState.totalItems >= customizationParams?.maxCustomizationsAreas) {
        setCustomizationsState({
          ...customizationsState,
          isUploadDisabled: true,
        });
      } else {
        setCustomizationsState({
          ...customizationsState,
          isUploadDisabled: false,
        });
      }
    }

    const specialGroup = viewerOptions.specialGroup;
    if (specialGroup && instanceOfViewerGroupAttribute(specialGroup) && customizationsState.totalItems !== undefined) {
      const itemCountAttribute = specialGroup.attributes?.find((attr) => attr.id === SPECIAL_OPTION.INVISIBLE_GROUP_CUSTOMPRICE_ATTRIBUTE);
      const option = itemCountAttribute?.options?.find((option) => option.id === String(customizationsState.totalItems));
      if (option) onSelectedOption(option);
    }
  }, [customizationsState.totalItems, customizationParams]);

  async function onAddTextItem(item: EditTextItem, areaId: number) {
    if (customizationsState.isUploadDisabled) return;

    await onAddItemText(item, areaId);
  }

  async function onAddImageItem(imageId: number, areaId: number) {
    if (customizationsState.isUploadDisabled) return;

    await onAddItemImage(imageId, areaId);
  }

  async function onItemRemove(guid: string) {
    await onRemoveItem(guid);
  }

  function onSelectOptions(item: ViewerItemInterface) {
    checkExploded(customizationParams?.explodedValues?.options, item.id);
    const Previewdata = {
      image: item.imageUrl,
      optionName: item.id,
      stepName: previewImage?.stepName,
      groupName: previewImage?.groupName,
      attributeName: previewImage?.attributeName,
    };
    setPreviewImage(Previewdata);
    onSelectedOption(item);
  }

  function checkExploded(options: string[] | undefined, id: string): void {
    if (hasExploded() && !!options) {
      if (options.indexOf(id) !== -1) {
        if (!isExploded) toggleExploded();
      } else if (isExploded) {
        toggleExploded();
      }
    }
  }

  const onArIconClick = async () => {
    if (isArMobile) {
      try {
        await showArMobile();
      } catch (ex) {
        console.error(ex);
      }
    } else {
      try {
        const res = await openArChoiceModal();
        if (res) {
          onPlatformSelected(res);
        }
      } catch (ex) {
        console.log('Modal Close');
      }
    }
  };

  const onExposedClick = () => {
    toggleExploded();
  };

  const onFullScreen = async () => {
    setIsFullscreen();
  };

  const onAddToCart = async () => {
    addToCart(
      {
        ...additionalProps,
        totalPrice,
        bulkData: bulkData?.attributes,
        customPrices: customPrices,
        customizationItems: customizationsState.totalItems,
      },
      async (data) => {
        // add the preview files to the additional properties
        //@ts-ignore
        return { ...data.additionalProperties, previewFiles: data.previewFiles };
      }
    );
  };

  const onShowCustomizationInfo = (info: ViewerCustomizationInterface) => {
    openImageModal({
      title: t('ENGRAVING_MODAL_TITLE'),
      description: t('ENGRAVING_MODAL_DESCRIPTION'),
      btnClose: t('CLOSE'),
      imageCssClass: styles.modal_engraving_image,
      url: info.imageUrl as string,
    });
  };

  const onShowOptionPreview = (option: ViewerOptionInterface) => {
    if (option.imageUrl)
      openImageModal({
        title: t(option.id),
        description: null,
        btnClose: t('CLOSE'),
        url: option.imageUrl,
        imageCssClass: styles.option_preview_modal_image,
      });
  };

  const onShare = async () => {
    const url = await getShareUrl(1000, (1000 / 16) * 9);
    openShareModal({
      url,
    }).then((shareType) => {
      if (shareType) {
        share(shareType, url);
      }
    });
  };

  const onPdf = async () => {
    const url = await getPdfUrl();
    openPdfModal({
      url,
    });
  };

  useEffect(() => {
    if (!!language && currentLanguage.get() !== language) {
      currentLanguage.set(language);
    }
  }, [language]);

  useEffect(() => {
    if (viewerState.groupId === BULK_PRICES.BULK_PRICES_GROUP_CODE) {
      const attribute = bulkData?.attributes?.find((attr) => attr.id === viewerState.attributeId);
      if (attribute) setBulkDataAttributePrice(attribute, rawPrice);
    }
  }, [rawPrice, viewerState.attributeId]);

  const getCustomPrices = (attributes: ViewerAttributeInputInterface[]) => {
    if (customPrices) {
      return getCustomIntegrationPrices(attributes);
    } else {
      return getInBuiltCustomPrices(attributes);
    }
  };

  const getInBuiltCustomPrices = (attributes: ViewerAttributeInputInterface[]) => {
    const totalPrices = attributes.map((attr) => {
      const attrBulkItems = attr.options.reduce((acc, option) => {
        return acc + Number(option.value);
      }, 0);

      const attrPrice = attrBulkItems === 0 ? 0 : attr.price * attrBulkItems;
      return attrPrice;
    });
    return totalPrices;
  };

  const getCustomIntegrationPrices = (attributes: ViewerAttributeInputInterface[]) => {
    const getCustomPrice = (attr: ViewerAttributeInputInterface) => {
      return customPrices[attr.id] ? customPrices[attr.id]?.product_price : attr.price;
    };

    const getCustomCustomizationPrice = (attr: ViewerAttributeInputInterface) => {
      return customPrices[attr.id] ? customPrices[attr.id]?.embrodery_price : 0;
    };

    const totalPrices = attributes.map((attr) => {
      const attrBulkItems = attr.options.reduce((acc, option) => {
        return acc + Number(option.value);
      }, 0);

      const attrCustomPrice = getCustomPrice(attr) ? Number(getCustomPrice(attr).replace(',', '.').replace(' ', '')) : 0;

      const totalCustomizations = customizationsState.totalItems ?? 1;
      const totalCustomizationsPrice = totalCustomizations * Number(getCustomCustomizationPrice(attr).replace(',', '.').replace(' ', ''));
      const itemPrice = attrCustomPrice + totalCustomizationsPrice;
      const attrPrice = attrBulkItems === 0 ? 0 : itemPrice * attrBulkItems;
      return attrPrice;
    });
    return totalPrices;
  };

  useEffect(() => {
    if (bulkData?.attributes) {
      const attributes = bulkData.attributes;

      const totalPrices = getCustomPrices(attributes);

      const totalPrice = totalPrices.reduce((acc, price) => {
        return acc + price;
      }, 0);

      const formatter = new Intl.NumberFormat(culture, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
      });
      if (totalPrice) setTotalPrice(formatter.format(totalPrice));
    }
  }, [bulkData, culture, currency, setTotalPrice]);

  useEffect(() => {
    if (totalPrice && totalPrice !== '0') {
      setPrice(totalPrice);
    } else if (price) setPrice(price);
  }, [totalPrice, price]);

  useEffect(() => {
    setTotalPrice(undefined);
    resetBulkDataValues();
  }, [customizationsState.totalItems]);

  useEffect(() => {
    async function loadCustomization() {
      if (viewerRef && viewerRef.current) {
        const urlParams = new URLSearchParams(window.location.search);
        const customizationId = urlParams.get('customizationId');
        const projectId = urlParams.get('projectId'); // this is the specific project name
        const productId = productCode;
        if (customizationId && productId) {
          const customization = await customizations.load({
            customizationId,
            productId,
            projectId,
          });
          customization.renderStyle(viewerRef.current);
          customization.setTranslation();
          setCustomizationAssets(customization.getAssets());
          setCustomizationParams(customization.getParams());
        }
      }
    }
    loadCustomization();
  }, [viewerRef, productCode, setCustomizationAssets]);

  useEffect(() => {
    if (!isLoading) {
      const urlParamsGet = new URLSearchParams(window.location.search);
      const customizationComposition = urlParamsGet.get('composition');
      if (customizationComposition) loadComposition(customizationComposition);
    }
  }, [isLoading, loadComposition]);

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        updateView();
      }, 300);
    }
    // eslint-disable-next-line
  }, [isFullscreen, isLoading]);

  //since zakeke responds with "isSceneReady" incorrectly at start. this implemention ignores their first response and then continues to work normally
  const firstRunFix = useRef(false);
  const [shouldShowScene, setShouldShowScene] = useState<boolean>(false);
  useEffect(() => {
    if (firstRunFix.current) {
      setShouldShowScene(isSceneReady);
    }
    if (!firstRunFix.current) {
      if (isSceneReady) {
        firstRunFix.current = true;
        setShouldShowScene(false);
      }
    }
  }, [isSceneReady]);

  useEffect(() => {
    if (!previewImage) {
      viewerOptions.groups.forEach((group) => {
        if (instanceOfViewerGroupAttribute(group)) {
          group.attributes.forEach((attr) => {
            attr.options.forEach((option) => {
              if (option.selected && !!option.imageUrl) {
                const Previewdata = {
                  image: option.imageUrl,
                  optionName: option.id,
                  attributeName: attr.id,
                  stepName: undefined,
                  groupName: group.id,
                };
                setPreviewImage(Previewdata);
              }
            });
          });
        }
        if (instanceOfViewerGroupStep(group)) {
          group.steps.forEach((step) => {
            step.attributes.forEach((attr) => {
              attr.options.forEach((option) => {
                if (option.selected && !!option.imageUrl) {
                  const Previewdata = {
                    image: option.imageUrl,
                    optionName: option.id,
                    attributeName: attr.id,
                    stepName: step.id,
                    groupName: group.id,
                  };
                  setPreviewImage(Previewdata);
                }
              });
            });
          });
        }
      });
    }
  }, [viewerOptions, previewImage]);
  /* const { viewerState } = useViewerState();
  console.log(viewerState);
  console.log(viewerOptions); */
  return (
    <div ref={viewerRef}>
      <Loader visible={isLoading || isArLoading || isShareLoading || customizationsState.isLoading || isPdfLoading} />
      <ARChoiceModal />
      <QrCodeModal />
      <ImageModal />
      <ShareModal />
      <PdfModal />

      {shouldShowScene && (
        <div className={styles.ff__root}>
          <AddTextDialog
            isVisible={customizationsState.showAddTextDialog}
            onClose={() => setCustomizationsState({ ...customizationsState, showAddTextDialog: false })}
            onConfirm={(item) => {
              onAddTextItem(item, customizationsState.actualAreaId);
              setCustomizationsState({ ...customizationsState, showAddTextDialog: false });
            }}
          />

          <ImagesGalleryDialog
            isVisible={customizationsState.imageGalery.showAddImageDialog}
            onClose={() =>
              setCustomizationsState({ ...customizationsState, imageGalery: { ...customizationsState.imageGalery, showAddImageDialog: false } })
            }
            onImageSelected={(image) => {
              if (customizationsState.imageGalery.currentAction === 'ADD') {
                onAddImageItem(image.imageID, customizationsState.actualAreaId);
              } else if (customizationsState.imageGalery.currentAction === 'EDIT') {
                onSetItemImage(customizationsState.imageGalery.selectedItem.guid, image.imageID);
              }
              setCustomizationsState({ ...customizationsState, imageGalery: { ...customizationsState.imageGalery, showAddImageDialog: false } });
            }}
          ></ImagesGalleryDialog>

          <ErrorDialog
            error={'Failed uploading image.'}
            onCloseClick={() => setCustomizationsState({ ...customizationsState, showErrorDialog: false })}
            isVisible={customizationsState.showErrorDialog}
          />
          <div className={styles.ff__viewer}>
            <div className={styles.ff__viewer__left_actions}>
              <Cameras cameras={viewerOptions.cameras} onSelect={onSelectedOption} />
              {!customizationParams?.previewHidden && <Preview PreviewImage={previewImage} />}
            </div>
            <div className={styles.ff__viewer__right_actions}>
              {isArEnable() && (
                <BubbleButton label={t('AR')} onClick={onArIconClick}>
                  <ViewerIcon>
                    <ArIcon />
                  </ViewerIcon>
                </BubbleButton>
              )}
              {hasExploded() && (
                <BubbleButton label={label} onClick={onExposedClick}>
                  <ViewerIcon>
                    <IconOpen />
                  </ViewerIcon>
                </BubbleButton>
              )}
              {isFullscreen !== 'not-supported' && (
                <BubbleButton label={t('FULL_SCREEN')} onClick={onFullScreen}>
                  <ViewerIcon>
                    <FullScreenIcon />
                  </ViewerIcon>
                </BubbleButton>
              )}
              <Zoom zoomIn={zoomIn} zoomOut={zoomOut} />
            </div>

            <div ref={fullScreenRef} className={classnames(styles.ff__viewer__zakeke)}>
              <ZakekeViewer />
            </div>
          </div>
          <Menu
            groups={viewerOptions.groups}
            price={price}
            isCartLoading={isCartLoading}
            saveText={onSaveText}
            saveFont={onSaveFont}
            onItemRemove={onItemRemove}
            onAddImageItem={onAddImageItem}
            showCustomizationInfo={onShowCustomizationInfo}
            addToCart={onAddToCart}
            showOptionPreview={onShowOptionPreview}
            share={onShare}
            pdf={onPdf}
            pageTitle={urlParams?.title || ''}
            pageDescription={urlParams?.productDescription || ''}
            optionSelected={onSelectOptions}
            customizationsState={customizationsState}
            setCustomizationsState={setCustomizationsState}
            bulkData={bulkData}
            setBulkDataOptionValue={setBulkDataOptionValue}
            disableCart={disableCart}
            descriptionFieldValue={urlParams?.descriptionFieldValue}
          />
        </div>
      )}
    </div>
  );
};

export default Viewer;
